// The inbox's reply-as-email panel (docs/06-ux-rules.md "Composer"), rendered
// by ThreadPane's `renderComposer` slot only for a viewer who can manage
// Feedback, sharing that pane's single `useInboxActions` instance.
// Progressive disclosure (FR-27 v11.8, v70 `.dmcomposer.collapsed`): rests
// collapsed to the message box; rules live in `useComposerDisclosure`.
// context: collapsed hides the title, labels, Subject, reward panel and bar;
//   the cannot-email notice and send-error banner always show. Supersedes
//   the chassis "always expanded and pinned" rule (G-7). A view-only viewer
//   never mounts this; ThreadPane renders its banner in the slot instead.
import { Button } from '@thanx/uikit/button'
import { Input } from '@thanx/uikit/input'
import { Radio, RadioGroup } from '@thanx/uikit/radio'
import { Text } from '@thanx/uikit/text'
import { TextArea } from '@thanx/uikit/textarea'
import type { FeedbackInboxDetail } from 'api/feedbackInbox.types'
import Gift from 'components/Icons/Gift'
import GiftAdd from 'components/Icons/GiftAdd'
import { PermissionTooltip } from 'components/Navbar/Bar/PermissionTooltip'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useCallback, useEffect, useRef } from 'react'
import { useStyletron } from 'styletron-react'
import { textUtils } from 'utilities/textUtils'
import type { ViewerCan } from '..'
import { COMPOSER_MAX_HEIGHT_PCT } from '../constants'
import type { UseInboxActionsResult } from '../ThreadPane/useInboxActions'
import ComposerBanner, { ComposerErrorBanner } from './ComposerBanner'
import OfferPicker from './OfferPicker'
import PointsInput, { maxPointsFor } from './PointsInput'
import type { AvailableOffer } from './useAvailableOffers'
import { useAvailableOffers } from './useAvailableOffers'
import { useComposerDisclosure } from './useComposerDisclosure'
import {
  MESSAGE_MAX_LENGTH,
  SUBJECT_MAX_LENGTH,
  useInboxComposerDraft,
} from './useInboxComposerDraft'

const t = buildTranslate('feedback_inbox.composer')
const validationT = buildTranslate('validations')

const MESSAGE_ROWS = 4
// v70 `.dmcomposer.collapsed #dmReply{min-height:44px}`; one row is ~42px
// with uikit's TextArea padding.
const COLLAPSED_MESSAGE_ROWS = 1

// `aria-controls` target for the Add/Remove reward toggle button (bottom
// action bar) — the block it controls sits above Subject, non-adjacent in
// the DOM, so the id link is load-bearing, not decorative.
const REWARD_PANEL_ID = 'feedback-inbox-composer-reward'

// uikit's STANDARD start-enhancer margin is 1rem, too loose for the AI-1439
// design. 7px + the glyph's ~2px viewBox padding reads as the mock's ~9px gap.
const REWARD_ICON_GAP_PX = '7px'
const REWARD_BUTTON_OVERRIDES = {
  StartEnhancer: { style: { marginRight: REWARD_ICON_GAP_PX } },
}

// aria-describedby targets for the over-cap errors. Each is also an
// always-mounted polite live region: text inserted into a pre-existing region
// is announced; a node mounted with its text already in it is not.
const SUBJECT_ERROR_ID = 'inbox-composer-subject-error'
const MESSAGE_ERROR_ID = 'inbox-composer-message-error'

// Same visible copy as SendMessageModal: components/Form/Input capitalises
// the shared validations.max_length string and ends it with a full stop.
const maxLengthError = (maxLength: number) =>
  textUtils.initialCapital(
    textUtils.toSentence({ array: [validationT('max_length', { maxLength })] })
  )

export interface InboxComposerProps {
  detail: FeedbackInboxDetail
  actions: UseInboxActionsResult
  viewerCan: ViewerCan
  // Fired after a send actually succeeds (docs/06-ux-rules.md "Composer" /
  // "Result") — best-effort: the composer itself typically unmounts first,
  // since `shouldShowComposer` flips false the instant the resolve
  // mutation's optimistic patch marks the item resolved.
  onSent?: () => void
  // Reports unsent-draft state up to the container's discard-confirm guard
  // (`useDiscardGuard`; docs/06-ux-rules.md "Discard-confirm", P6 D10) — the
  // container intercepts a selection change BEFORE it happens, so it needs
  // to know this ahead of time, not react to `detail.id` after the fact.
  onDirtyChange?: (isDirty: boolean) => void
}

const InboxComposer = ({
  detail,
  actions,
  viewerCan,
  onSent,
  onDirtyChange,
}: InboxComposerProps) => {
  const [css] = useStyletron()
  const {
    offers,
    isLoading: isLoadingOffers,
    isError: isOffersError,
  } = useAvailableOffers(detail.kind, detail.id)

  // True for exactly the render caused by the operator's own RadioGroup
  // click below — never on this component's initial mount (Offer is the
  // default radio) or on an item switch that resets back to Offer
  // underneath them (`useInboxComposerDraft`'s `resetDraft`). Reset back to
  // false right after every render so a later, unrelated re-render (or
  // remount) can't replay a stale "true" (see `OfferPicker`'s own doc
  // comment on its `autoFocus` prop).
  const userSwitchedToOfferRef = useRef(false)
  useEffect(() => {
    userSwitchedToOfferRef.current = false
  })

  const {
    rewardType,
    selectedOffer,
    points,
    subject,
    message,
    setSubject,
    setMessage,
    setPoints,
    setSelectedOffer,
    handleRewardTypeChange,
    handleRemoveReward,
    hasReward,
    isDirty,
    subjectTooLong,
    messageTooLong,
    isOverLength,
    pointsInvalid,
    send,
  } = useInboxComposerDraft({ detail, actions, onSent, onDirtyChange })

  const messageRef = useRef<HTMLTextAreaElement>(null)
  const composerRef = useRef<HTMLDivElement>(null)
  const { expanded, expand, rewardOpen, setRewardOpen } = useComposerDisclosure(
    {
      itemId: detail.id,
      rootRef: composerRef,
      canCollapse: !isDirty,
    }
  )

  // v70 pickReward: the search field unmounts on pick, so hand focus to the
  // next step (the message) rather than dropping it on <body>.
  const handleOfferSelect = useCallback(
    (offer: AvailableOffer) => {
      setSelectedOffer(offer)
      messageRef.current?.focus()
    },
    [setSelectedOffer]
  )

  // `viewerCan.manage` isn't read here: `ThreadPane` never mounts this
  // component for a view-only viewer at all (v70 hides the composer
  // entirely — see ThreadPane/index.tsx's `shouldShowComposer` header
  // comment), so `manage` is always true whenever `InboxComposer` renders.
  const canGrant = viewerCan.grant
  // S4 (AI-1353): SendMessageModal's recipient model. Only `absent` blocks;
  // a `masked` address is hidden from this operator but still delivered
  // server-side. No `loading` state: it ships with the detail.
  const canEmail = detail.customer.emailState !== 'absent'

  const fieldsDisabled = !canEmail
  const rewardControlsDisabled = fieldsDisabled || !canGrant

  // Unsent content is never hidden, and a disabled (cannot-email) textarea
  // can't take the focus that expands, so both render expanded.
  const isExpanded = expanded || isDirty || !canEmail
  const showRewardPanel = isExpanded && rewardOpen

  const rewardTooltipReason = !canGrant
    ? t('grant_permission_tooltip')
    : undefined

  const hasContent = message.trim() !== '' || hasReward
  const canSend =
    hasContent &&
    !isOverLength &&
    !pointsInvalid &&
    !actions.isPending &&
    canEmail

  const handleSend = () => {
    if (!canSend) return
    send()
  }

  // Collapsing the disclosure CLEARS the selection (user decision) — no
  // hidden reward may survive into the send payload, so this resets
  // offer/points exactly as `handleRemoveReward` already does for the old
  // always-visible "Remove reward" action.
  const handleToggleReward = () => {
    if (rewardOpen) {
      handleRemoveReward()
    }
    setRewardOpen(open => !open)
  }

  return (
    <div
      ref={composerRef}
      onFocus={expand}
      onMouseDown={expand}
      className={css({
        backgroundColor: palette.white,
        border: `1px solid ${palette.grey30}`,
        borderBottom: 'none',
        borderRadius: '14px 14px 0 0',
        // mock v70 .ib-convo .dmcomposer shadow — no shadow token in palette
        boxShadow: '0 -4px 16px rgba(20, 28, 38, 0.07)',
        padding: '16px 22px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        flexShrink: 0,
        // Safety valve (docs/06-ux-rules.md "Composer" / "Panel"): on a
        // short viewport the composer scrolls WITHIN itself rather than
        // pushing Send below the thread pane's visible frame.
        maxHeight: `${COMPOSER_MAX_HEIGHT_PCT}%`,
        overflowY: 'auto',
      })}
    >
      {isExpanded && (
        <Text.BodyText4 tag="h3" bold className="m-0">
          {t('title')}
        </Text.BodyText4>
      )}

      {!canEmail && (
        <ComposerBanner tone="neutral" role="note">
          {t('cannot_email_notice')}
        </ComposerBanner>
      )}

      <ComposerErrorBanner actions={actions} />

      {showRewardPanel && (
        <div
          id={REWARD_PANEL_ID}
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          })}
        >
          <RadioGroup
            aria-label={t('reward_type_group_aria')}
            align="horizontal"
            value={rewardType}
            disabled={rewardControlsDisabled}
            onChange={value => {
              const next = value === 'points' ? 'points' : 'offer'
              userSwitchedToOfferRef.current = next === 'offer'
              handleRewardTypeChange(next)
            }}
          >
            <Radio value="offer" className="pr-m">
              {t('offer_radio')}
            </Radio>
            <Radio value="points">{t('points_radio')}</Radio>
          </RadioGroup>

          {rewardType === 'offer' ? (
            <OfferPicker
              offers={offers}
              isLoading={isLoadingOffers}
              isError={isOffersError}
              selectedId={selectedOffer?.id ?? null}
              onSelect={handleOfferSelect}
              onClear={handleRemoveReward}
              disabled={rewardControlsDisabled}
              autoFocus={userSwitchedToOfferRef.current}
            />
          ) : (
            <PointsInput
              value={points}
              onChange={setPoints}
              disabled={rewardControlsDisabled}
              max={maxPointsFor(detail.kind)}
            />
          )}
        </div>
      )}

      {isExpanded && (
        <div>
          <Text.BodyText4 tag="p" bold className="mb-xs">
            {t('subject_label')}
          </Text.BodyText4>
          <Input
            value={subject}
            onChange={setSubject}
            aria-label={t('subject_label')}
            disabled={fieldsDisabled}
            // baseui `error`, not uikit `errored` (see PointsInput): styling + aria-invalid.
            error={subjectTooLong}
            aria-describedby={subjectTooLong ? SUBJECT_ERROR_ID : undefined}
          />
          <div id={SUBJECT_ERROR_ID} aria-live="polite" aria-atomic="true">
            {subjectTooLong && (
              <Text.BodyText5 color="negative">
                {maxLengthError(SUBJECT_MAX_LENGTH)}
              </Text.BodyText5>
            )}
          </div>
        </div>
      )}

      <div>
        {isExpanded && (
          <Text.BodyText4 tag="p" bold className="mb-xs">
            {t('message_label')}
          </Text.BodyText4>
        )}
        <TextArea
          inputRef={messageRef}
          value={message}
          onChange={setMessage}
          aria-label={t('message_label')}
          placeholder={t('message_placeholder', {
            firstName: detail.customer.firstName ?? '',
          })}
          disabled={fieldsDisabled}
          rows={isExpanded ? MESSAGE_ROWS : COLLAPSED_MESSAGE_ROWS}
          error={messageTooLong}
          aria-describedby={messageTooLong ? MESSAGE_ERROR_ID : undefined}
        />
        <div id={MESSAGE_ERROR_ID} aria-live="polite" aria-atomic="true">
          {messageTooLong && (
            <Text.BodyText5 color="negative">
              {maxLengthError(MESSAGE_MAX_LENGTH)}
            </Text.BodyText5>
          )}
        </div>
      </div>

      {isExpanded && (
        // v70 `.dmc-bar` (prototype index.html:662): the reward toggle sits
        // hard left, Send hard right — `space-between`, not the `flex-end`
        // pairing the old optional "Remove reward" button used.
        <div
          className={css({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
          })}
        >
          <PermissionTooltip
            disabled={rewardControlsDisabled}
            reason={rewardTooltipReason}
            id="feedback-inbox-composer-reward-perm"
            inline
            // The button carries its own accessible name ("Add reward" /
            // "Remove reward") — srOnlyReason keeps that as the name and
            // exposes the permission reason via aria-describedby instead of
            // letting the wrapper's role="note"+aria-label override it (see
            // PermissionTooltip's own doc comment on this prop).
            srOnlyReason
          >
            <Button
              kind="secondary"
              onClick={handleToggleReward}
              disabled={rewardControlsDisabled}
              aria-expanded={rewardOpen}
              aria-controls={REWARD_PANEL_ID}
              overrides={REWARD_BUTTON_OVERRIDES}
              startEnhancer={() => (rewardOpen ? <Gift /> : <GiftAdd />)}
            >
              {rewardOpen ? t('remove_reward') : t('add_reward')}
            </Button>
          </PermissionTooltip>
          <Button kind="primary" onClick={handleSend} disabled={!canSend}>
            {t('send')}
          </Button>
        </div>
      )}
    </div>
  )
}

export default InboxComposer
